(() => {
    'use strict';

    const CARD_SELECTOR = '.jdgm-reviews-grid-widget .jdgm-review-card';
    const TOGGLE_SELECTOR = '.jdgm-review-card__custom-toggle';

    function getLabel(expanded) {
        return expanded ? 'Collapse review' : 'Expand review';
    }

    function updateButtonState(card, button) {
        const expanded = card.classList.contains('is-expanded');
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.setAttribute('aria-label', expanded ? 'Collapse review' : 'Expand review');
    }

    function toggleCard(card, button) {
        card.classList.toggle('is-expanded');
        updateButtonState(card, button);
    }

    function createToggleButton(card) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'jdgm-review-card__custom-toggle';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', 'Expand review');

        button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            card.classList.toggle('is-expanded');
            updateButtonState(card, button);
        });

        return button;
    }

    function ensurePlaceholderImage(card) {
        const placeholderSrc = window.JDGM_REVIEW_PLACEHOLDER;
        if (!placeholderSrc) return;

        const existingMedia = card.querySelector('.jdgm-review-card-media');
        const preview = card.querySelector('.jdgm-review-card__preview');
        const details = card.querySelector('.jdgm-review-card__details');

        if (!preview || !details) return;

        if (existingMedia) {
            const img = existingMedia.querySelector('.jdgm-review-card-media__image');

            if (img) {
                const currentSrc = (img.getAttribute('src') || '').trim();

                if (!currentSrc) {
                    img.setAttribute('src', placeholderSrc);
                }

                img.addEventListener(
                    'error',
                    () => {
                        img.setAttribute('src', placeholderSrc);
                    },
                    { once: true }
                );
            }

            details.classList.add('jdgm-review-card__details--media');
            card.classList.add('jdgm-review-card--has-media');

            return;
        }

        const media = document.createElement('div');
        media.className = 'jm-frame jdgm-review-card-media';
        media.style.aspectRatio = '1 / 1';

        const img = document.createElement('img');
        img.className = 'jdgm-review-card-media__image jdgm-review-card-media__image--placeholder';
        img.src = placeholderSrc;
        img.alt = 'Review image';
        img.loading = 'lazy';

        img.addEventListener(
            'error',
            () => {
                img.setAttribute('src', placeholderSrc);
            },
            { once: true }
        );

        media.appendChild(img);

        card.insertBefore(media, preview);

        details.classList.add('jdgm-review-card__details--media');
        card.classList.add('jdgm-review-card--has-media', 'jdgm-review-card--placeholder-media');
    }

    function ensureTitleVisibility(card) {
        const detailsBody = card.querySelector('.jdgm-review-card__details-body .jm-stack');
        const description = card.querySelector('.jdgm-review-card__details-description');

        if (!detailsBody || !description) return;

        const existingTitle = detailsBody.querySelector('.jdgm-review-card__custom-title');
        if (existingTitle) return;

        const nativeTitle =
            card.querySelector('.jdgm-review-card__details-title') ||
            card.querySelector('[class*="title"]');

        if (nativeTitle && nativeTitle.textContent.trim() !== '') {
            nativeTitle.classList.add('jdgm-review-card__custom-title');
            return;
        }

        const ariaLabel = card.getAttribute('aria-label') || '';
        const titleMatch = ariaLabel.match(/\. (.+)$/);
        const extractedTitle = titleMatch ? titleMatch[1].trim() : '';

        if (extractedTitle && extractedTitle.toLowerCase() !== 'no title') {
            const titleEl = document.createElement('p');
            titleEl.className = 'jdgm-review-card__custom-title';
            titleEl.textContent = extractedTitle;
            detailsBody.insertBefore(titleEl, description);
        }
    }

    function ensureExpandablePreviewContent(card) {
        const previewFooter = card.querySelector('.jdgm-review-card__footer-inner--preview');
        const detailsDescription = card.querySelector('.jdgm-review-card__details-description');
        const detailsTitle = card.querySelector('.jdgm-review-card__details-title');

        if (!previewFooter || !detailsDescription) return;
        if (previewFooter.querySelector('.jdgm-review-card__preview-expandable')) return;

        const expandable = document.createElement('div');
        expandable.className = 'jdgm-review-card__preview-expandable';

        const titleText = detailsTitle ? detailsTitle.textContent.trim() : '';
        const descriptionText = detailsDescription ? detailsDescription.textContent.trim() : '';

        if (titleText) {
            const titleEl = document.createElement('p');
            titleEl.className = 'jdgm-review-card__preview-title';
            titleEl.textContent = titleText;
            expandable.appendChild(titleEl);
        }

        if (descriptionText) {
            const textEl = document.createElement('p');
            textEl.className = 'jdgm-review-card__preview-description';
            textEl.textContent = descriptionText;
            expandable.appendChild(textEl);
        }

        const meta = document.createElement('div');
        meta.className = 'jdgm-review-card__preview-meta';

        const rating = previewFooter.querySelector('.jm-star-rating');
        const reviewer = previewFooter.querySelector('.jdgm-review-card__reviewer-name-wrapper');

        if (rating) meta.appendChild(rating);
        if (reviewer) meta.appendChild(reviewer);

        previewFooter.innerHTML = '';
        previewFooter.appendChild(expandable);
        previewFooter.appendChild(meta);
    }

    function setupCard(card) {
        if (card.dataset.customized === 'true') return;

        const details = card.querySelector('.jdgm-review-card__details');
        if (!details) return;

        ensureTitleVisibility(card);
        ensurePlaceholderImage(card);
        ensureExpandablePreviewContent(card);

        const button = createToggleButton(card);
        card.appendChild(button);

        card.dataset.customized = 'true';
    }

    function setupAllCards(root = document) {
        root.querySelectorAll(CARD_SELECTOR).forEach(setupCard);
    }

    function initObserver() {
        const widgetRoot = document.querySelector(
            '.shopify-app-block .jdgm-reviews-grid-widget, .jdgm-widget.jdgm-reviews-grid-widget'
        );

        if (!widgetRoot) return;

        setupAllCards(widgetRoot);

        const observer = new MutationObserver((mutations) => {
            let shouldRefresh = false;

            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length) {
                    shouldRefresh = true;
                }
            });

            if (shouldRefresh) {
                setupAllCards(widgetRoot);
            }
        });

        observer.observe(widgetRoot, {
            childList: true,
            subtree: true,
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initObserver);
    } else {
        initObserver();
    }
})();